import { cloneDeep, isEqual } from 'lodash-es';
import { Subject, Subscription } from 'rxjs';
import { EmitterEvent } from '../emitter.service';
import { CanvasMouseHandler } from './canvas-mouse-handler';

export const DEFAULT_CANVAS_CURSOR = 'move';
export const DEFAULT_OBJECT_CURSOR = 'pointer';
export const DEFAULT_CANVAS_FILL = '#FFFFFF';

// http://www.senocular.com/flash/tutorials/transformmatrix/
export const A = 0;
export const B = 1;
export const C = 2;
export const D = 3;
export const TX = 4;
export const TY = 5;

export const ZOOM_MAX = 20;
export const ZOOM_DELTA = 4;
export const ZOOM_FACTOR = 6;
export const SCROLL_SENSITIVITY = 0.001;
export const MAX_SCALE_PERCENTAGE_MINIMAL = 10;
export const MAX_SCALE_PERCENTAGE_PARTIAL = 15;

export enum CanvasDisplayPrecision {
  FULL = 'FULL',
  PARTIAL = 'PARTIAL',
  MINIMAL = 'MINIMAL',
}

export interface Bounds {
  minX?: number;
  maxX?: number;
  minY?: number;
  maxY?: number;
  width?: number;
  height?: number;
}

export interface CanvasZoomState {
  renderingCtx?: CanvasRenderingCtx;
  canReset: boolean;
  canZoomIn: boolean;
  canZoomOut: boolean;
}

export interface CanvasCoordinates {
  x: number;
  y: number;
  midPoint?: number;
}

export interface CanvasRectangle extends CanvasCoordinates {
  w: number;
  h: number;
}

export const createEmptyBounds = (): Bounds => ({
  minX: Number.MAX_VALUE,
  minY: Number.MAX_VALUE,
  maxX: -Number.MAX_VALUE,
  maxY: -Number.MAX_VALUE,
  width: 0,
  height: 0,
});

export const rotateCoordinates = (coords: CanvasCoordinates, rotation: number): CanvasCoordinates => {
  const { x, y } = coords;

  const rad = (rotation * Math.PI) / 180;
  const rotatedX = x * Math.cos(rad) + y * Math.sin(rad);
  const rotatedY = -x * Math.sin(rad) + y * Math.cos(rad);

  return { x: rotatedX, y: rotatedY };
};

export const screenToCanvas = (canvas: HTMLCanvasElement, coords: CanvasCoordinates, ctx: CanvasRenderingCtx): CanvasCoordinates => {
  const transform = new DOMMatrix();
  if (ctx.rx) {
    const width = canvas.width / devicePixelRatio / 2;
    const height = canvas.height / devicePixelRatio / 2;

    transform.translateSelf(width, height);
    transform.rotateSelf(ctx.rx);
    transform.translateSelf(-width, -height);
  }

  transform.translateSelf(ctx.tx, ctx.ty);
  transform.scaleSelf(ctx.sx, ctx.sy);

  const itransform = transform.inverse();
  const rect = canvas.getBoundingClientRect();
  const onCanvas = {
    x: coords.x - rect.x,
    y: coords.y - rect.y,
  };

  return {
    x: onCanvas.x * itransform.a + onCanvas.y * itransform.c + itransform.e,
    y: onCanvas.x * itransform.b + onCanvas.y * itransform.d + itransform.f,
  };
};

export const updateBounds = (b: Bounds, ...v: CanvasCoordinates[]): void => {
  v.forEach((c: CanvasCoordinates) => {
    if (c.x < b.minX) {
      b.minX = c.x;
    }

    if (c.y < b.minY) {
      b.minY = c.y;
    }

    if (c.x > b.maxX) {
      b.maxX = c.x;
    }

    if (c.y > b.maxY) {
      b.maxY = c.y;
    }
  });

  b.width = b.maxX - b.minX;
  b.height = b.maxY - b.minY;
};

export interface CanvasRenderingCtx {
  precision: CanvasDisplayPrecision;
  tx: number;
  ty: number;
  rx: number;
  sx: number;
  sy: number;
}

export abstract class CanvasRenderer {
  abstract draw(renderingCtx: CanvasRenderingCtx): void;
}

interface CanvasResizeEvent {
  width: number;
  height: number;
  resetZoom: boolean;
}

export abstract class AbstractCanvasDecorator {
  protected defaultRenderingCtx: CanvasRenderingCtx = { sx: 1, sy: 1, tx: 0, ty: 0, rx: 0, precision: CanvasDisplayPrecision.FULL };
  protected renderingCtx: CanvasRenderingCtx = { sx: 1, sy: 1, tx: 0, ty: 0, rx: 0, precision: CanvasDisplayPrecision.FULL };
  protected ctx: CanvasRenderingContext2D;
  protected mouseHandler: CanvasMouseHandler;

  animating: boolean;
  sizeChanged = new Subject<EmitterEvent<CanvasZoomState>>();
  zoomChanged = new Subject<EmitterEvent<CanvasZoomState>>();
  mouseMoved = new Subject<EmitterEvent<CanvasCoordinates>>();
  mouseDragged = new Subject<EmitterEvent<CanvasCoordinates>>();
  mouseReleased = new Subject<EmitterEvent<CanvasCoordinates>>();
  protected defaultZoom: number;

  protected animationHandle = 0;

  private readonly resizeEvent: Subject<CanvasResizeEvent> = new Subject<CanvasResizeEvent>();
  private readonly resizeSub: Subscription;
  protected destroyed = false;

  protected renderedBounds = createEmptyBounds();

  protected constructor(
    protected readonly canvas: HTMLCanvasElement,
    protected readonly enableMouseHandler = true,
    protected readonly onlyClickable = false,
    protected readonly debugInfos = false,
  ) {
    const width = canvas.parentElement.clientWidth;
    const height = canvas.parentElement.clientHeight;

    this.resizeCanvas(width, height);

    this.ctx = this.canvas.getContext('2d', { alpha: false });
    this.ctx.fillStyle = DEFAULT_CANVAS_FILL;
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    if (enableMouseHandler) {
      this.mouseHandler = new CanvasMouseHandler(this, canvas, debugInfos, onlyClickable);
    }

    this.resizeSub = this.resizeEvent.subscribe((event: CanvasResizeEvent) => this.canvasContainerResized(event));
  }

  protected abstract renderComponents(): void;
  abstract searchPossibleTargets(eventCoordinates: CanvasCoordinates): any;
  abstract selectTarget(target: any, coordinates: CanvasCoordinates): void;
  abstract zoomToTarget(): void;

  getCursor(eventCoordinates: CanvasCoordinates): string {
    const target = this.searchPossibleTargets(eventCoordinates);

    let cursor = DEFAULT_CANVAS_CURSOR;
    if (target) {
      cursor = DEFAULT_OBJECT_CURSOR;
    }

    return cursor;
  }

  requestRenderAll(): void {
    if (!this.animationHandle) {
      this.renderAll();
    }
  }

  renderAll(): void {
    if (!this.destroyed) {
      this.ctx.resetTransform();
      this.ctx.fillStyle = DEFAULT_CANVAS_FILL;
      this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
      this.ctx.scale(devicePixelRatio, devicePixelRatio);

      if (this.renderingCtx.rx) {
        this.ctx.translate(this.canvasWidth() / 2, this.canvasHeight() / 2);
        this.ctx.rotate(this.renderingCtx.rx * (Math.PI / 180));
        this.ctx.translate(-this.canvasWidth() / 2, -this.canvasHeight() / 2);
      }

      this.ctx.translate(this.renderingCtx.tx, this.renderingCtx.ty);
      this.ctx.scale(this.renderingCtx.sx, this.renderingCtx.sy);

      this.renderComponents();

      if (this.animating) {
        this.animationHandle = requestAnimationFrame(() => this.renderAll());
      } else {
        delete this.animationHandle;
      }
    }
  }

  resize(width: number, height: number, resetZoom = false): void {
    if (!this.destroyed) {
      this.resizeEvent.next({ width, height, resetZoom });
    }
  }

  resetZoom(render = true): void {
    if (!this.destroyed) {
      this.updateDefaultZoom();

      const centerPoint = this.getRenderedCenterCoords(this.defaultZoom);

      this.defaultRenderingCtx.sx = this.defaultZoom;
      this.defaultRenderingCtx.sy = -this.defaultZoom;
      this.defaultRenderingCtx.tx = -centerPoint.x;
      this.defaultRenderingCtx.ty = centerPoint.y + this.canvasHeight();
      this.renderingCtx = cloneDeep(this.defaultRenderingCtx);

      this.updateDisplayPrecision();

      if (render) {
        this.renderAll();
        this.zoomChanged.next({ value: this.currentZoom() } as EmitterEvent);
      }
    }
  }

  zoomIn(): void {
    const rect = this.canvas.getBoundingClientRect();
    const center = { x: rect.x + this.canvasWidth() / 2, y: rect.y + this.canvasHeight() / 2 };
    this.zoom(center, -ZOOM_DELTA * ZOOM_FACTOR);
  }

  zoomOut(): void {
    const rect = this.canvas.getBoundingClientRect();
    const center = { x: rect.x + this.canvasWidth() / 2, y: rect.y + this.canvasHeight() / 2 };
    this.zoom(center, ZOOM_DELTA * ZOOM_FACTOR);
  }

  pan(offsetX: number, offsetY: number): void {
    if (!this.renderingCtx.rx) {
      this.renderingCtx.tx += offsetX;
      this.renderingCtx.ty += offsetY;
    } else {
      const { x, y } = rotateCoordinates({ x: offsetX, y: offsetY }, this.renderingCtx.rx);
      this.renderingCtx.tx += x;
      this.renderingCtx.ty += y;
    }

    this.requestRenderAll();
    this.zoomChanged.next({ value: this.currentZoom() } as EmitterEvent);
  }

  zoom(coords: CanvasCoordinates, delta: number): void {
    console.log(`Zooming on new coords.`, JSON.stringify(coords), delta);

    let zoom = this.renderingCtx.sx;
    zoom -= delta * SCROLL_SENSITIVITY;

    if (zoom > ZOOM_MAX) {
      zoom = ZOOM_MAX;
    }

    if (zoom < this.defaultZoom) {
      zoom = this.defaultZoom;
    }

    const beforeTransform = screenToCanvas(this.canvas, coords, this.renderingCtx);

    this.renderingCtx.sx = zoom;
    this.renderingCtx.sy = -zoom;

    const afterTransform = screenToCanvas(this.canvas, coords, this.renderingCtx);

    const offsetX = afterTransform.x - beforeTransform.x;
    const offsetY = afterTransform.y - beforeTransform.y;
    this.renderingCtx.tx += offsetX * zoom;
    this.renderingCtx.ty += offsetY * -zoom;

    this.updateDisplayPrecision();
    this.renderAll();
    this.zoomChanged.next({ value: this.currentZoom() } as EmitterEvent);
  }

  currentZoom(): CanvasZoomState {
    const zoom = this.renderingCtx.sx;
    const zoomChanged = zoom !== this.defaultZoom;
    const renderingCtxChanged = !isEqual({ ...this.renderingCtx, precision: CanvasDisplayPrecision.FULL }, this.defaultRenderingCtx);
    const canReset = zoomChanged || renderingCtxChanged;
    const canZoomIn = zoom < ZOOM_MAX;
    const canZoomOut = zoom > this.defaultZoom;

    return {
      renderingCtx: this.renderingCtx,
      canReset,
      canZoomIn,
      canZoomOut,
    };
  }

  destroy(): void {
    if (this.animationHandle) {
      cancelAnimationFrame(this.animationHandle);
      this.animationHandle = 0;
    }
    this.animating = false;
    this.renderedBounds = null;
    this.resizeSub?.unsubscribe();
    this.ctx = null;
    this.mouseHandler?.destroy();
    this.mouseHandler = null;
    this.destroyed = true;
  }

  rotateCw(): void {
    this.rotate(90);
  }

  rotateCcw(): void {
    this.rotate(-90);
  }

  protected applyZoom(coords: CanvasCoordinates, zoomFactor: number): void {
    const vpCenterX = this.canvasWidth() / 2;
    const vpCenterY = this.canvasHeight() / 2;

    const targetCenterX = coords.x;
    const targetCenterY = coords.y;

    const x = Math.round(targetCenterX * zoomFactor - vpCenterX); // x is the location where the center of the viewport should be
    const y = Math.round(targetCenterY * zoomFactor - vpCenterY); // y idem

    this.renderingCtx.sx = zoomFactor;
    this.renderingCtx.sy = -zoomFactor;
    this.renderingCtx.tx = -x;
    this.renderingCtx.ty = y + this.canvasHeight();

    this.updateDisplayPrecision();
    this.renderAll();
    this.zoomChanged.next({ value: this.currentZoom() } as EmitterEvent);
  }

  protected canvasContainerResized(event: CanvasResizeEvent): void {
    const { width, height, resetZoom } = event;

    const offsetX = width - this.canvasWidth();
    this.renderingCtx.tx += offsetX / 2;

    this.resizeCanvas(width, height);

    if (resetZoom || !this.defaultZoom) {
      this.resetZoom(true);
    } else {
      this.updateDefaultZoom();
      this.updateDisplayPrecision();
      this.renderAll();

      const changedEvent = { value: this.currentZoom() };
      this.zoomChanged.next(changedEvent);
      this.sizeChanged.next(changedEvent);
    }
  }

  protected updateDisplayPrecision(): void {
    const sx = this.renderingCtx.sx * 100;
    let nextPrecision = CanvasDisplayPrecision.FULL;

    if (sx < MAX_SCALE_PERCENTAGE_MINIMAL) {
      nextPrecision = CanvasDisplayPrecision.MINIMAL;
    } else if (sx < MAX_SCALE_PERCENTAGE_PARTIAL) {
      nextPrecision = CanvasDisplayPrecision.PARTIAL;
    }

    if (this.renderingCtx.precision !== nextPrecision) {
      this.renderingCtx.precision = nextPrecision;
    }
  }

  protected canvasWidth(): number {
    return this.canvas.width / devicePixelRatio;
  }

  protected canvasHeight(): number {
    return this.canvas.height / devicePixelRatio;
  }

  private getRenderedCenterCoords(zoom: number): CanvasCoordinates {
    const vpCenterX = this.canvasWidth() / 2;
    const vpCenterY = this.canvasHeight() / 2;

    const baysCenterX = this.renderedBounds.minX + this.renderedBounds.width / 2;
    const baysCenterY = this.renderedBounds.minY + this.renderedBounds.height / 2;

    const x = Math.round(baysCenterX * zoom - vpCenterX); // x is the location where the center of the viewport should be
    const y = Math.round(baysCenterY * zoom - vpCenterY); // y idem
    return { x, y };
  }

  private rotate(angle: number): void {
    this.renderingCtx.rx = (this.renderingCtx.rx + angle) % 360;
    this.defaultRenderingCtx.rx = this.renderingCtx.rx;

    this.updateDefaultZoom();
    this.renderAll();
    this.zoomChanged.next({ value: this.currentZoom() });
  }

  private updateDefaultZoom(): void {
    // On veut se garder une marge de 10% autour
    const rx = Math.abs(this.renderingCtx.rx);
    const isRotatedVertically = rx === 90 || rx === 270;

    const height = this.canvasHeight();
    const width = this.canvasWidth();

    const w = isRotatedVertically ? height : width;
    const h = isRotatedVertically ? width : height;

    const hRatio = w / (this.renderedBounds.width + this.renderedBounds.width * 0.1);
    const vRatio = h / (this.renderedBounds.height + this.renderedBounds.height * 0.1);
    this.defaultZoom = Math.min(hRatio, vRatio);
  }

  private resizeCanvas(width: number, height: number): void {
    this.canvas.width = width * devicePixelRatio;
    this.canvas.height = height * devicePixelRatio;
    this.canvas.style.width = width + 'px';
    this.canvas.style.height = height + 'px';
  }
}
